/**
 * AppLogger - Application Core Logging (v2 Taxonomy)
 *
 * Taxonomy: app.{operation}[.{flavor}]
 *
 * Operations:
 * - init: App initialization
 * - auth: Authentication events
 * - permissions: Permission checks
 * - preferences: System preferences
 * - component: Component lifecycle
 *
 * @example
 * AppLogger.init.start();
 * AppLogger.auth.keycloakSuccess({ realm: 'test' });
 * AppLogger.permissions.parsed({ roles: ['admin'] });
 */

import { WeBelieveLogger, captureCallerSource } from '../WeBelieveLogger';

export const AppLogger = {
  // ============================================================================
  // App Initialization
  // ============================================================================
  init: {
    start: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.init.start', 'Application initialization started', source, data);
    },

    complete: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.init.complete', 'Application initialized', source, data);
    },

    componentMount: (componentName: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.init.component', `${componentName} mounted`, source, { component: componentName, ...data });
    },
  },

  // ============================================================================
  // Authentication
  // ============================================================================
  auth: {
    keycloakInit: (firmId: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.auth.keycloak-init', `Initializing Keycloak for firm: ${firmId}`, source, { firmId, ...data });
    },

    keycloakRefresh: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.auth.keycloak-refresh', `[KEYCLOAK] ${message}`, source, data);
    },

    keycloakEvent: (event: string, eventData?: unknown, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.auth.keycloak-event', `onKeycloakEvent (AppInitializer) ${event} ${eventData ?? ''}`, source, { event, eventData, ...data });
    },

    keycloakTokens: (hasTokens: boolean, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.auth.keycloak-tokens', `onKeycloakTokens (AppInitializer)`, source, { hasTokens, ...data });
    },

    tokenParsed: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.auth.token-parsed', '[EdgeLex] Parsed token', source, data);
    },

    success: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.auth.success', '[AppInitializer] Keycloak authentication successful', source, data);
    },

    tokenRegistered: (realm: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.auth.token-registered', `✅ [App] Registered Keycloak token getter with Axios for realm: ${realm}`, source, { realm, ...data });
    },

    loggingConfigInit: (realm: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.auth.logging-config', `✅ [App] LoggingConfigService initialized for realm: ${realm}`, source, { realm, ...data });
    },
  },

  // ============================================================================
  // Permissions
  // ============================================================================
  permissions: {
    check: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.check', '🔍 useUserPermissions called', source, data);
    },

    tokenMemo: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.token-memo', '🔍 keycloakToken useMemo called', source, data);
    },

    tokenReturn: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.token-return', '🔍 keycloakToken returning', source, data);
    },

    permissionsMemo: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.permissions-memo', '🔍 permissions useMemo called with keycloakToken', source, data);
    },

    parseRoles: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.parse-roles', '🔍 parseKeycloakRoles called', source, data);
    },

    tokenStructure: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.token-structure', '🔍 Full token structure', source, data);
    },

    realmRoles: (roles: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.realm-roles', `🔍 Extracted realm roles: ${roles.length} roles`, source, { roles, ...data });
    },

    realmAccess: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.realm-access', '🔍 Token realm_access', source, data);
    },

    resourceAccess: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.resource-access', '🔍 Token resource_access', source, data);
    },

    clientRoles: (roles: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.client-roles', `🔍 Client roles: ${roles.length} roles`, source, { roles, ...data });
    },

    allRoles: (roles: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.all-roles', `🔍 All extracted roles: ${roles.length} roles`, source, { roles, ...data });
    },

    groups: (groups: string[], data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.groups', `🔍 Groups: ${groups.length} groups`, source, { groups, ...data });
    },

    userRoles: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.user-roles', '🔍 User Roles from Keycloak', source, data);
    },

    permissionsReturn: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.permissions.return', '🔍 permissions useMemo returning', source, data);
    },
  },

  // ============================================================================
  // System Preferences
  // ============================================================================
  preferences: {
    loading: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.preferences.loading', '[SystemPreferences] Loading firm-wide preferences...', source, data);
    },

    loaded: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.preferences.loaded', '[SystemPreferences] Preferences loaded', source, data);
    },

    fontApplied: (font: string, fontStack: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.preferences.font-applied', `[SystemPreferences] Applied system font: ${font} → ${fontStack}`, source, { font, fontStack, ...data });
    },

    fontSkipped: (reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.preferences.font-skipped', `[SystemPreferences] ${reason}`, source, data);
    },

    scaleApplied: (scale: string, factor: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.preferences.scale-applied', `[SystemPreferences] Applied font scale: ${scale} → ${factor}`, source, { scale, factor, ...data });
    },

    applied: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'app.preferences.applied', '[SystemPreferences] ✅ Preferences applied to DOM', source, data);
    },
  },

  // ============================================================================
  // Component Lifecycle
  // ============================================================================
  component: {
    running: (componentName: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'app.component.running', `🟢 ${componentName} IS RUNNING 🟢`, source, { component: componentName, ...data });
    },
  },
};
